import { SurveyType } from '../../codegen/graphql';
import { SURVEY_TYPE_OPTIONS } from '../../constants/surveyTypes';

export type SurveyScopeFilterZone = {
    id: string;
    name: string;
};

export type SurveyScopeFilterStore = {
    id: string;
    storeNo?: string | null;
};

export type SurveyTableColumnFilters = {
    scopeFilter: string[] | null;
    onScopeFilterChange: (value: string[] | null) => void;
    scopeFilterOptions: string[];
    scopeFilterLoading: boolean;
    showScopeFilter: boolean;
    surveyTypeFilter: string[] | null;
    onSurveyTypeFilterChange: (value: string[] | null) => void;
    statusFilter: string[] | null;
    onStatusFilterChange: (value: string[] | null) => void;
    statusFilterOptions: string[];
};

export const SURVEY_TYPE_FILTER_OPTIONS = [
    'All',
    ...SURVEY_TYPE_OPTIONS.map(option => option.label),
];

const SURVEY_SCOPE_ZONE_OPTION_PREFIX = 'Zone: ';
const SURVEY_SCOPE_STORE_OPTION_PREFIX = 'Store: ';

export function buildSurveyScopeFilterOptions(
    zones: SurveyScopeFilterZone[],
    stores: SurveyScopeFilterStore[],
): string[] {
    const zoneOptions: string[] = [];
    zones.forEach(zone => {
        const name = zone.name.trim();
        if (!name) {
            return;
        }
        zoneOptions.push(`${SURVEY_SCOPE_ZONE_OPTION_PREFIX}${name}`);
    });
    zoneOptions.sort((left, right) => left.localeCompare(right));

    const storeOptions: string[] = [];
    stores.forEach(store => {
        const storeNo = store.storeNo?.trim() ?? '';
        if (!storeNo) {
            return;
        }
        storeOptions.push(`${SURVEY_SCOPE_STORE_OPTION_PREFIX}${storeNo}`);
    });
    storeOptions.sort((left, right) => left.localeCompare(right));

    return ['All', ...zoneOptions, ...storeOptions];
}

export function mapScopeFilterLabelsToIds(
    labels: string[] | null,
    zones: SurveyScopeFilterZone[],
    stores: SurveyScopeFilterStore[],
): { advertisingZoneIds?: string[]; storeIds?: string[] } {
    if (!labels || labels.length === 0) {
        return {};
    }

    const zoneIdsByName = new Map<string, string>();
    zones.forEach(zone => {
        const name = zone.name.trim();
        if (name) {
            zoneIdsByName.set(name, zone.id);
        }
    });

    const storeIdsByStoreNo = new Map<string, string>();
    stores.forEach(store => {
        const storeNo = store.storeNo?.trim();
        if (storeNo) {
            storeIdsByStoreNo.set(storeNo, store.id);
        }
    });

    const advertisingZoneIds: string[] = [];
    const storeIds: string[] = [];
    labels.forEach(label => {
        if (label === 'All') {
            return;
        }

        if (label.startsWith(SURVEY_SCOPE_ZONE_OPTION_PREFIX)) {
            const name = label
                .slice(SURVEY_SCOPE_ZONE_OPTION_PREFIX.length)
                .trim();
            const zoneId = zoneIdsByName.get(name);
            if (zoneId) {
                advertisingZoneIds.push(zoneId);
            }
            return;
        }

        if (label.startsWith(SURVEY_SCOPE_STORE_OPTION_PREFIX)) {
            const storeNo = label
                .slice(SURVEY_SCOPE_STORE_OPTION_PREFIX.length)
                .trim();
            const storeId = storeIdsByStoreNo.get(storeNo);
            if (storeId) {
                storeIds.push(storeId);
            }
        }
    });

    const mapped: { advertisingZoneIds?: string[]; storeIds?: string[] } = {};
    if (advertisingZoneIds.length > 0) {
        mapped.advertisingZoneIds = advertisingZoneIds;
    }
    if (storeIds.length > 0) {
        mapped.storeIds = storeIds;
    }
    return mapped;
}

export function mapSurveyTypeFilterLabels(
    labels: string[] | null,
): SurveyType[] | undefined {
    if (!labels || labels.length === 0) {
        return undefined;
    }

    const matched: SurveyType[] = [];
    labels.forEach(label => {
        if (label === 'Top 20') {
            matched.push(SurveyType.Top_20);
        } else if (label === 'Category') {
            matched.push(SurveyType.Category);
        } else if (label === 'Special Project') {
            matched.push(SurveyType.SpecialProject);
        }
    });

    if (matched.length === 0) {
        return undefined;
    }
    return matched;
}
